/**
 * Global Search — /app/search
 *
 * Searches across the user's current workspace via the centralized
 * /api/search endpoint. Permission scoping is enforced on the backend
 * (require_workspace_access + per-record workspace filter).
 *
 * Features:
 *   • Debounced input (300ms)
 *   • Type filter chips
 *   • Loading / empty / error states
 *   • Each result links to the correct in-app record
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Search as SearchIcon,
  Loader2,
  Video,
  StickyNote,
  ListTodo,
  CalendarClock,
  FileText,
  Sparkles,
  Users,
  CheckSquare,
  X as XIcon,
} from "lucide-react";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatLongDateTimeInTz, userBrowserTimezone } from "@/lib/time";

const FILTERS = [
  { value: "", label: "All", types: undefined },
  { value: "meetings", label: "Meetings", types: "meetings" },
  { value: "bookings", label: "Bookings", types: "bookings" },
  { value: "appointment_types", label: "Appointments", types: "appointment_types" },
  { value: "notes", label: "Notes", types: "notes" },
  { value: "tasks", label: "Tasks", types: "tasks" },
  { value: "files", label: "Files", types: "files" },
  { value: "summaries", label: "Summaries", types: "summaries" },
  { value: "transcripts", label: "Transcripts", types: "transcripts" },
  { value: "contacts", label: "Contacts", types: "contacts" },
  { value: "action_items", label: "Action items", types: "action_items" },
  { value: "decisions", label: "Decisions", types: "decisions" },
];

const ICON_BY_TYPE = {
  meeting: Video,
  note: StickyNote,
  task: ListTodo,
  booking_link: CalendarClock,
  booking_submission: CalendarClock,
  appointment_type: CalendarClock,
  summary: Sparkles,
  transcript: FileText,
  contact: Users,
  action_item: CheckSquare,
  decision: FileText,
  file: FileText,
};
const LABEL_BY_TYPE = {
  meeting: "Meeting",
  note: "Note",
  task: "Task",
  booking_link: "Booking link",
  booking_submission: "Booking",
  appointment_type: "Appointment type",
  summary: "AI summary",
  transcript: "Transcript",
  contact: "Contact",
  action_item: "Action item",
  decision: "Decision",
  file: "File",
};

export default function GlobalSearch() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tz = user?.timezone || userBrowserTimezone();

  const initialQ = params.get("q") || "";
  const initialType = params.get("type") || "";
  const [q, setQ] = useState(initialQ);
  const [typeFilter, setTypeFilter] = useState(initialType);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const debRef = useRef(null);
  const inputRef = useRef(null);

  // Autofocus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!q.trim() || !currentWorkspace) {
      setResults([]);
      setLoading(false);
      setSearched(false);
      return;
    }
    setLoading(true);
    setError("");
    debRef.current = setTimeout(async () => {
      try {
        const reqParams = {
          q: q.trim(),
          workspace_id: currentWorkspace.workspace_id,
        };
        if (typeFilter) reqParams.types = typeFilter;
        const { data } = await api.get(`/search`, { params: reqParams });
        setResults(data.results || []);
        setSearched(true);
        // Keep URL in sync so the search is shareable / refresh-safe
        const next = new URLSearchParams();
        next.set("q", q.trim());
        if (typeFilter) next.set("type", typeFilter);
        setParams(next, { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Search failed");
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debRef.current);
  }, [q, typeFilter, currentWorkspace?.workspace_id]); // eslint-disable-line

  const grouped = useMemo(() => {
    const out = new Map();
    for (const r of results) {
      const key = LABEL_BY_TYPE[r.type] || r.type;
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(r);
    }
    return out;
  }, [results]);

  return (
    <div className="flex flex-col gap-6" data-testid="search-page">
      <header>
        <p className="bloq-eyebrow">Search</p>
        <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">Find anything in Bloq.</h1>
        <p className="text-sm text-[#82828c] mt-3 max-w-2xl">
          Search across meetings, bookings, notes, tasks, AI summaries, transcripts,
          contacts, action items, and decisions in this workspace.
        </p>
      </header>

      <div className="relative max-w-2xl">
        <SearchIcon className="w-4 h-4 text-[#82828c] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search Bloq…"
          className="bloq-input pl-9 pr-9 w-full"
          data-testid="search-input"
        />
        {q && (
          <button
            onClick={() => setQ("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#f5efe6]"
            data-testid="search-clear"
            aria-label="Clear search"
          >
            <XIcon className="w-3.5 h-3.5 text-[#82828c]" />
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="search-filters">
        {FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            onClick={() => setTypeFilter(f.value)}
            className={`px-3 py-1.5 text-xs ${
              typeFilter === f.value
                ? "bg-[#005e7a] text-white"
                : "bg-white border border-[#e5e0d6] text-[#4a4a52] hover:bg-[#f5efe6]"
            }`}
            data-testid={`search-filter-${f.value || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#82828c]" data-testid="search-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Searching…
        </div>
      )}

      {error && !loading && (
        <p className="text-sm text-[#c43838]" data-testid="search-error">{error}</p>
      )}

      {!loading && !error && !q.trim() && (
        <p className="text-sm text-[#82828c]" data-testid="search-empty-prompt">
          Type something to start searching.
        </p>
      )}

      {!loading && !error && q.trim() && results.length === 0 && searched && (
        <div className="bloq-card p-10 text-center" data-testid="search-no-results">
          <SearchIcon className="w-8 h-8 mx-auto text-[#82828c]" />
          <p className="bloq-headline text-xl mt-3 mb-1">No results</p>
          <p className="text-sm text-[#82828c]">No records match &ldquo;{q}&rdquo; in this workspace.</p>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="flex flex-col gap-6">
          {Array.from(grouped.entries()).map(([label, items]) => (
            <section key={label} data-testid={`search-group-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <h2 className="bloq-headline text-sm uppercase tracking-widest text-[#82828c] mb-3">
                {label} <span className="text-[#82828c]/70">({items.length})</span>
              </h2>
              <div className="flex flex-col gap-2">
                {items.map((r) => {
                  const Icon = ICON_BY_TYPE[r.type] || SearchIcon;
                  return (
                    <ResultRow key={`${r.type}-${r.id || r.title}-${r.date || Math.random()}`} r={r} Icon={Icon} tz={tz} navigate={navigate} />
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultRow({ r, Icon, tz, navigate }) {
  const onOpen = () => {
    if (r.url) navigate(r.url);
  };
  return (
    <button
      onClick={onOpen}
      className="bloq-card p-4 text-left hover:-translate-y-0.5 transition-transform flex items-start gap-3"
      data-testid={`search-result-${r.type}-${r.id || "x"}`}
    >
      <span className="w-9 h-9 bg-[#f5efe6] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#005e7a]" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="bloq-headline text-base truncate">{r.title || "(untitled)"}</p>
          <span className="text-[10px] uppercase tracking-widest font-bold bg-[#005e7a]/10 text-[#005e7a] px-1.5 py-0.5">
            {LABEL_BY_TYPE[r.type] || r.type}
          </span>
          {r.status && (
            <span className="text-[10px] uppercase tracking-widest text-[#82828c]">{r.status}</span>
          )}
        </div>
        {r.snippet && (
          <p className="text-sm text-[#4a4a52] mt-1 line-clamp-2 break-words">{r.snippet}</p>
        )}
        {r.date && (
          <p className="text-[11px] text-[#82828c] mt-1.5">{formatLongDateTimeInTz(r.date, tz)}</p>
        )}
      </div>
    </button>
  );
}
